import { Alert, AlertDescription, AlertTitle } from "@shared/ui/alert";
import { Button } from "@shared/ui/button";
import { Tabs } from "@shared/ui/tabs"; // Using the URL-based tab service
import { ChevronLeft, Info } from "lucide-react";
import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useConsumerListSummary, usePaperBillSummary } from "../../hooks";
import { ConsumerTable } from "./ConsumerTable";
import { MetricsCard } from "./MetricsCard";
import { PaperBillDistribution } from "./PaperBillDistribution";

interface ConsumerDistributionViewProps {
  cycle: any;
  onBack: () => void;
}

export function ConsumerDistributionView({
  cycle,
  onBack,
}: ConsumerDistributionViewProps) {
  const params = useParams();
  const { data: summary } = useConsumerListSummary(params.id);
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab2");
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);

  const { cycleId } = useParams<{ cycleId: string }>();

  const handleMetricClick = (metric: string) => {
    setSelectedMetric((prev) => (prev === metric ? null : metric));
  };
  // Configure tab components for the URL-based tab service
  const tabComponents = {
    "consumer-list": {
      label: "Consumer List",
      component: (
        <>
          <Alert
            variant="default"
            className="bg-muted border border-input mb-4"
          >
            <Info className="h-4 w-4" />
            <AlertTitle>Tip</AlertTitle>
            <AlertDescription className="text-xs">
              Each row shows consumer bill info and distribution status per
              channel. Click 'Actions' to view or download the individual bill.
            </AlertDescription>
          </Alert>

          <ConsumerTable activeFilter={selectedMetric} />
        </>
      ),
    },
    "paper-distribution": {
      label: "Paper Bill Distribution",
      component: <PaperBillDistribution cycleId={cycleId || ""} />,
    },
  };

  const { data: summaryData, isLoading: summaryLoading } = usePaperBillSummary(
    params.id
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onBack}>
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <h2 className="text-xl font-semibold">
          Consumer Distribution: {cycle?.cycleName || `Cycle ${cycleId}`}
        </h2>
      </div>

      {activeTab === "consumer-list" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <MetricsCard
            title="Total Consumers"
            value={summary?.totalConsumer}
            className={
              selectedMetric === "totalConsumers" ? "ring-2 ring-primary" : ""
            }
          />
          <MetricsCard
            title="Fully Delivered"
            value={summary?.fullyDelivered}
            variant="success"
            className={
              selectedMetric === "fullyDelivered" ? "ring-2 ring-primary" : ""
            }
          />
          <MetricsCard
            title="At Least One Delivered"
            value={summary?.atLeastOneDelivered}
            variant="info"
            className={
              selectedMetric === "atLeastOneDelivered"
                ? "ring-2 ring-primary"
                : ""
            }
          />
          <MetricsCard
            title="Failed All"
            value={summary?.failedAll}
            variant={summary?.failedAll > 0 ? "destructive" : "default"}
            className={
              selectedMetric === "failedAll" ? "ring-2 ring-primary" : ""
            }
          />
          <MetricsCard
            title="Missing Contact"
            value={summary?.missingContact}
            variant={summary?.missingContact > 0 ? "warning" : "default"}
            className={
              selectedMetric === "missingContact" ? "ring-2 ring-primary" : ""
            }
          />
        </div>
      )}

      {activeTab === "paper-distribution" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricsCard
            title="Total Bills"
            value={summaryData?.totalBills || 0}
            icon="printer"
            variant="default"
          />
          <MetricsCard
            title="Assigned Bills"
            value={summaryData?.assignedBills || 0}
            icon="calendar"
            variant="info"
          />
          <MetricsCard
            title="Successfully Distributed"
            value={summaryData?.successfullyDistributed || 0}
            icon="calendar"
            variant="success"
          />
          <MetricsCard
            title="Distribution Pending"
            value={summaryData?.distributionPending || 0}
            icon="alert"
            variant={
              (summaryData?.distributionPending || 0) > 0
                ? "warning"
                : "default"
            }
          />
        </div>
      )}

      <Tabs
        defaultValue="consumer-list"
        tabComponents={tabComponents}
        className="w-full"
        idPrefix="consumer-distribution"
        level={2}
      />
    </div>
  );
}
